import React from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const EMPTY_SKU_FILTERS = { search: '', brand: 'ALL', category: 'ALL', tier: 'ALL', channel: 'ALL', kind: 'ALL', gap: 'ALL' };

const GAPS = [
  ['missing_cost', 'Missing cost'], ['missing_price', 'Missing price'], ['missing_codes', 'No marketplace codes'],
  ['missing_asin', 'Missing ASIN'], ['missing_fsn', 'Missing FSN'],
];
const SORTS = [['sku_code', 'SKU code'], ['product_name', 'Product name'], ['cost_price', 'Cost'], ['selling_price', 'Price'], ['created_at', 'Newest']];

const FilterSelect = ({ id, value, onChange, all, options, width = 'w-[150px]' }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-9 ${width} text-[13px]`} data-testid={`sku-filter-${id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="ALL" data-testid={`sku-filter-${id}-all`}>{all}</SelectItem>
      {options.map(([val, label]) => <SelectItem key={val} value={val} data-testid={`sku-filter-${id}-${val}`}>{label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const SkuFilters = ({ filters, onChange, facets, sort, onSort, showCost = false }) => {
  // Cost-based filter and sort are for admins only (the server ignores them for others).
  const gaps = showCost ? GAPS : GAPS.filter(([val]) => val !== 'missing_cost');
  const sorts = showCost ? SORTS : SORTS.filter(([val]) => val !== 'cost_price');
  const set = key => value => onChange({ ...filters, [key]: value });
  const active = Object.entries(filters).some(([key, value]) => value !== EMPTY_SKU_FILTERS[key]);
  const pairs = list => (list || []).map(v => [v, v]);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="sku-filter-bar">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input value={filters.search} onChange={e => set('search')(e.target.value)} placeholder="SKU, product, old code, ASIN, FSN…"
          className="h-9 w-[260px] border-0 bg-gray-50 pl-8 text-[13px]" data-testid="search-input" />
      </div>
      <FilterSelect id="brand" value={filters.brand} onChange={set('brand')} all="All brands" options={pairs(facets?.brands)} />
      <FilterSelect id="category" value={filters.category} onChange={set('category')} all="All categories" options={pairs(facets?.categories)} width="w-[170px]" />
      <FilterSelect id="tier" value={filters.tier} onChange={set('tier')} all="All tiers" options={['A', 'B', 'C', 'D'].map(t => [t, `Tier ${t}`])} width="w-[110px]" />
      <FilterSelect id="channel" value={filters.channel} onChange={set('channel')} all="Any channel" options={(facets?.channels || []).map(c => [c.value, `Listed on ${c.label}`])} width="w-[190px]" />
      <FilterSelect id="kind" value={filters.kind} onChange={set('kind')} all="Singles & bundles" options={[['single', 'Singles only'], ['bundle', 'Bundles only']]} />
      <FilterSelect id="gap" value={filters.gap} onChange={set('gap')} all="Any catalogue gaps" options={gaps} width="w-[180px]" />
      <Select value={sort} onValueChange={onSort}>
        <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid="sku-sort"><SelectValue /></SelectTrigger>
        <SelectContent>{sorts.map(([val, label]) => <SelectItem key={val} value={val} data-testid={`sku-sort-${val}`}>Sort: {label}</SelectItem>)}</SelectContent>
      </Select>
      {active && <Button variant="ghost" size="sm" className="h-9 text-[12px]" onClick={() => onChange(EMPTY_SKU_FILTERS)} data-testid="sku-clear-filters"><X className="mr-1 h-3.5 w-3.5" />Clear filters</Button>}
    </div>
  );
};
